/* Admin web terminal. Pairs with the server's exec WebSocket: binary frames are
   raw terminal bytes, text frames are JSON control messages. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { Pill } from '../../components/ui';

type ConnState = 'connecting' | 'connected' | 'disconnected';

interface ControlFrame {
  type?: 'exit' | 'error';
  message?: string;
  code?: number | null;
}

const STATUS: Record<ConnState, { label: string; tone: 'muted' | 'emerald' | 'red' }> = {
  connecting: { label: 'Connecting…', tone: 'muted' },
  connected: { label: 'Connected', tone: 'emerald' },
  disconnected: { label: 'Disconnected', tone: 'red' },
};

export function DockerExecPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const label = searchParams.get('name') || id;

  const hostRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const closeTimer = useRef<number>();
  const [status, setStatus] = useState<ConnState>('connecting');

  const sendResize = useCallback(() => {
    const term = termRef.current;
    const ws = wsRef.current;
    if (!term || !ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'resize', cols: term.cols, rows: term.rows }));
  }, []);

  // Exec sessions are stateful, so there is no auto-reconnect — only this.
  const connect = useCallback(() => {
    const term = termRef.current;
    if (!term) return;

    const previous = wsRef.current;
    if (previous) {
      previous.onclose = null;
      previous.onerror = null;
      previous.close();
    }

    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${location.host}/api/admin/docker/containers/${encodeURIComponent(id)}`
      + `/exec?cols=${term.cols}&rows=${term.rows}`;
    const ws = new WebSocket(url);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    setStatus('connecting');

    ws.onopen = () => { setStatus('connected'); term.focus(); };

    ws.onmessage = (ev: MessageEvent<string | ArrayBuffer>) => {
      if (typeof ev.data === 'string') {
        let msg: ControlFrame;
        try { msg = JSON.parse(ev.data) as ControlFrame; } catch { return; }
        if (msg.type === 'error') {
          term.write(`\r\n\x1b[31m[${msg.message ?? 'error'}]\x1b[0m\r\n`);
        } else if (msg.type === 'exit') {
          const code = msg.code == null ? '' : `, exit code ${msg.code}`;
          term.write(`\r\n\x1b[90m[session ended${code}]\x1b[0m\r\n`);
        }
        return;
      }
      term.write(new Uint8Array(ev.data));
    };

    ws.onclose = () => setStatus('disconnected');
    ws.onerror = () => setStatus('disconnected');
  }, [id]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const term = new Terminal({
      cursorBlink: true,
      fontFamily: 'monospace',
      fontSize: 13,
      theme: { background: '#000000' },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host);
    fit.fit();
    termRef.current = term;

    const encoder = new TextEncoder();
    const dataSub = term.onData((data) => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(encoder.encode(data));
    });
    const resizeSub = term.onResize(sendResize);
    const onWindowResize = () => fit.fit();
    window.addEventListener('resize', onWindowResize);

    // The flex host may not have its final height on the first layout pass.
    const frame = requestAnimationFrame(() => fit.fit());

    connect();

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onWindowResize);
      dataSub.dispose();
      resizeSub.dispose();
      const ws = wsRef.current;
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.close();
        wsRef.current = null;
      }
      term.dispose();
      termRef.current = null;
    };
  }, [connect, sendResize]);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  const onClose = () => {
    wsRef.current?.close();
    window.close();
    // window.close() is a no-op for tabs the script did not open
    closeTimer.current = window.setTimeout(() => navigate(-1), 100);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', padding: '1rem', gap: '0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600, margin: 0 }}>Console</h2>
          <span className="mono muted" style={{ fontSize: '0.85rem' }}>{label}</span>
          <Pill tone={STATUS[status].tone}>{STATUS[status].label}</Pill>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {status === 'disconnected' && (
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() => { termRef.current?.reset(); connect(); }}
            >Reconnect</button>
          )}
          <button type="button" className="btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>

      <div
        ref={hostRef}
        style={{
          flex: 1, minHeight: '320px', background: '#000',
          border: '1px solid var(--ha-border)', borderRadius: '0.5rem',
          overflow: 'hidden', padding: '0.5rem',
        }}
      />
    </div>
  );
}
